import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { StyleSheet, View, TextInput, Pressable, Platform, Keyboard } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import { BORDER_RADIUS, MEMORY_CHAR_LIMIT } from '@/constants/config';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

const isWeb = Platform.OS === 'web';
const CHAR_COUNT_WARNING_COLOR = '#F59E0B';

function getCharCountProps(length, limit) {
  const ratio = length / limit;
  if (ratio > 0.9) return { color: 'danger', style: undefined };
  if (ratio > 0.75) return { color: undefined, style: { color: CHAR_COUNT_WARNING_COLOR } };
  return { color: 'secondary', style: undefined };
}

function MemoryQuickAdd({
  text,
  onChangeText,
  onAddMemory,
  onCancel,
  isCreating = false,
}) {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const styles = useMemo(() => createStyles(colors, palette, isXS), [colors, palette, isXS]);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const inspirationChips = useMemo(() => [
    t('pages.memory.inspiration1'),
    t('pages.memory.inspiration2'),
    t('pages.memory.inspiration3'),
  ], [t]);

  const handleChipPress = useCallback((chip) => {
    if (isCreating) return;
    onChangeText(text.trim() ? `${text.trim()}. ${chip}` : chip);
    inputRef.current?.focus();
  }, [text, onChangeText, isCreating]);

  const handleSubmit = useCallback(async () => {
    if (!text.trim() || text.length > MEMORY_CHAR_LIMIT || isCreating) return;
    const ok = await onAddMemory(text.trim());
    if (ok) {
      onChangeText('');
      Keyboard.dismiss();
    }
  }, [text, isCreating, onAddMemory, onChangeText]);

  const handleKeyDown = useCallback((e) => {
    if (!isWeb || isCreating) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel?.();
    }
  }, [handleSubmit, onCancel, isCreating]);

  const charProps = getCharCountProps(text.length, MEMORY_CHAR_LIMIT);
  const isTooLong = text.length > MEMORY_CHAR_LIMIT;
  const canSubmit = text.trim().length > 0 && !isTooLong && !isCreating;

  return (
    <View style={styles.card}>
      <View style={[styles.inputWrapper, isFocused && styles.inputWrapperFocused]}>
        <TextInput
          ref={inputRef}
          style={styles.textInput}
          value={text}
          onChangeText={onChangeText}
          editable={!isCreating}
          accessibilityLabel={t('pages.memory.quickAddTitle')}
          placeholder={t('pages.memory.inputPlaceholder')}
          placeholderTextColor={colors.color_input_placeholder || colors.text_color_tertiary}
          maxLength={MEMORY_CHAR_LIMIT + 50}
          multiline
          numberOfLines={3}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyPress={handleKeyDown}
        />
        <View style={styles.inputMetaRow}>
          <Text size="xxs" color="tertiary">
            {isWeb ? t('pages.memory.saveShortcut') : ''}
          </Text>
          <Text size="xxs" color={charProps.color} style={charProps.style}>
            {text.length} / {MEMORY_CHAR_LIMIT}
          </Text>
        </View>
      </View>

      <View style={styles.inspirationSection}>
        <Text size="xxs" color="tertiary">{t('pages.memory.inspirationTitle')}</Text>
        <View style={styles.chipsRow}>
          {inspirationChips.map((chip) => (
            <Pressable
              key={chip}
              style={({ hovered }) => [
                styles.chip,
                hovered && styles.chipHovered,
              ]}
              accessibilityRole="button"
              disabled={isCreating}
              onPress={() => handleChipPress(chip)}
            >
              <Text size="xs" color="secondary">{chip}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View style={styles.footer}>
        <Button
          text={t('common.cancel')}
          variant="transparent"
          isDisabled={isCreating}
          size="medium"
          onPress={onCancel}
        />
        <Button
          text={t('pages.memory.addMemory')}
          prependIcon="plus"
          variant="primary"
          size="medium"
          isDisabled={!canSubmit}
          isLoading={isCreating}
          onPress={handleSubmit}
          iconColor={palette.onInk}
          textStyle={{ color: palette.onInk, fontWeight: '600' }}
          hoverBackgroundColor={palette.ink}
          style={{
            backgroundColor: palette.ink,
            borderRadius: 10,
            opacity: canSubmit ? 1 : 0.38,
          }}
        />
      </View>
    </View>
  );
}

const createStyles = (colors, palette, isXS) =>
  StyleSheet.create({
    card: {
      borderRadius: 18,
      borderWidth: 1,
      borderColor: palette.border,
      backgroundColor: palette.surface,
      padding: isXS ? 14 : 18,
      marginBottom: 20,
    },
    inputWrapper: {
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: palette.border,
      backgroundColor: palette.control,
      padding: 12,
    },
    inputWrapperFocused: {
      borderColor: palette.activeBorder,
    },
    textInput: {
      fontSize: 15,
      lineHeight: 22,
      color: colors.text_color_primary,
      minHeight: 72,
      textAlignVertical: 'top',
      ...(isWeb ? { outlineStyle: 'none' } : {}),
    },
    inputMetaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 8,
    },
    inspirationSection: {
      gap: 8,
      marginTop: 14,
    },
    chipsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
    },
    chip: {
      paddingVertical: 5,
      paddingHorizontal: 8,
      borderRadius: BORDER_RADIUS.small,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    chipHovered: {
      backgroundColor: palette.hover,
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 8,
      marginTop: 14,
    },
  });

export default React.memo(MemoryQuickAdd);
